/**
 * Masking-pattern validation — single source of truth (PSTUDIO-4123).
 *
 * A masking rule is a regular expression the Pype widget compiles at masking
 * time with `new RegExp(source, 'g')`
 * (`packages/apps/pype/src/core/masking-rules.ts:75`). That compile is wrapped
 * in a try/catch which caches `null` and skips the rule, so an uncompilable
 * pattern does not fail loudly — it silently never masks. This module is the
 * client-side check that keeps such a pattern from being saved in the first
 * place.
 *
 * The `'g'` flag mirrors the runtime's call. Measured, it changes no verdict on
 * its own — `g` affects `lastIndex`, not whether a source parses — so what keeps
 * the two in step is passing the same source string; the flag is what keeps them
 * in step if the runtime ever adds `u` or `v`, which DO change what parses.
 *
 * A pattern that compiles can still be unusable, which is the second check
 * (PSTUDIO-4300): `(a+)+$` is a legal regex that backtracks catastrophically,
 * and `maskText` runs every rule of a set over every outbound message on the
 * visitor's main thread. `masking-rules.ts:110-118` deliberately deferred that
 * guard to "where a human authors the regex", which is this screen.
 *
 * **Why the budget is empirical rather than structural.** The obvious check is
 * star height — refuse a quantifier nested inside a quantifier, as `safe-regex`
 * does. It cannot be used here: the product's OWN seeds are built from
 * `(?:\d[ -]*?){12}`, a quantifier inside a quantifier, so a structural rule
 * refuses the credit-card and SSN patterns the backend seeds into every rule
 * set. Measured instead, on a 24-character probe input (min of three runs,
 * Node 24, this machine):
 *
 * | pattern                                   | cost      |
 * |-------------------------------------------|-----------|
 * | both seeds, email, phone, ZIP, IBAN, name | 0.001 ms  |
 * | `^(\w+\s?)*$` (cheapest catastrophic)     | 110 ms    |
 * | `(a\|a)*$` (dearest)                      | 304 ms    |
 *
 * A 7000x gap is what makes a fixed millisecond budget safe on hardware whose
 * speed is unknown, and it is asserted in the suite rather than only recorded
 * here.
 *
 * Three limitations, all real:
 *
 * - **Engines differ.** This compiles in the author's browser; the widget
 *   compiles in each visitor's. A construct the author's engine accepts and an
 *   older one refuses (lookbehind, `\p{...}`) still passes here and is still
 *   skipped there.
 * - **The probe is a sample, not a proof.** Cost is measured against a fixed set
 *   of synthetic inputs at one length. A pattern that is cheap on all of them and
 *   explodes on a longer or differently-shaped string passes — the check buys the
 *   shapes that are common, not a guarantee. It fails OPEN by construction, which
 *   is the right direction for a gate standing in front of a product form.
 * - **The verdict is timing, so it is not bit-exact.** The budget sits ~1000x
 *   above every legitimate pattern measured and ~10x below the cheapest
 *   catastrophic one, and each input is measured twice so a single GC pause
 *   cannot condemn a pattern. On hardware 100x slower than this machine a
 *   legitimate pattern would still pass; on hardware 10x faster a catastrophic
 *   one could slip through, and it would then be caught by nothing else.
 */

export type MaskingPatternValidity = {
  valid: boolean;
  passedPresenceCheck: boolean;
  passedCompileCheck: boolean;
  passedComplexityCheck: boolean;
  /** No invisible character (PSTUDIO-4123, S10) — see `containsInvisibleCharacters`. */
  passedCharactersCheck: boolean;
};

/**
 * Characters that render as nothing and that `String.prototype.trim` does NOT
 * strip (except U+FEFF, which it does, so that one only matters inside the text):
 * soft hyphen, Mongolian vowel separator, zero-width space, word joiner and the
 * invisible math operators (U+2060-U+2064), and the byte-order mark.
 *
 * U+200C (ZWNJ) and U+200D (ZWJ) are deliberately NOT here: they are invisible
 * too, but they are letter-forming characters in Persian, Arabic, Hindi and
 * other scripts, so refusing them would reject ordinary names in the app's own
 * `ar` and `hi` locales. The set is a decision about those two, not a claim to be
 * every invisible code point Unicode has.
 */
const INVISIBLE_CHARACTERS = /[\u00AD\u180E\u200B\u2060-\u2064\uFEFF]/;

/**
 * Refused rather than stripped. The validator's stance since PSTUDIO-3007 is to
 * surface bad input instead of cleaning it up quietly, and deleting a character
 * from a regular expression changes what the rule matches. Used for BOTH the
 * rule name and the pattern, so the two fields cannot disagree about what is
 * allowed.
 */
export const containsInvisibleCharacters = (text: string): boolean =>
  INVISIBLE_CHARACTERS.test(text);

/**
 * One definition of a name the user may save, for a rule AND for a rule set:
 * non-blank once trimmed, and free of invisible characters. `trim()` alone is
 * not enough — it leaves U+200B in place, so a name made of one reads as
 * non-empty and is saved as a set or a rule that renders blank (PSTUDIO-4123 for
 * rules, PSTUDIO-4492 for rule sets).
 */
export const isUsableRuleName = (name: string): boolean => {
  const trimmed = name.trim();

  return trimmed !== '' && !containsInvisibleCharacters(trimmed);
};

/** What the inline rename of a rule set does with the edit it was handed. */
export type RuleSetRenameDecision =
  /** Keep the field open: the edit is not finished (the message is showing). */
  | { action: 'hold' }
  /** Close the field; send `rename` as the new name when there is one. */
  | { action: 'close'; rename?: string };

/**
 * The rename of a rule set, decided apart from the component because the page
 * imports the design system and no unit test can render it.
 *
 * - An UNCHANGED name always closes harmlessly, whatever it holds: a set saved
 *   with an invisible character by an earlier defect must not trap the user in a
 *   field they opened and left untouched.
 * - A blank name closes without a write, as it always did.
 * - A changed name with an invisible character is never written. On Enter, the
 *   explicit gesture, the field stays open and the message says why; on blur the
 *   user has left, so the edit is abandoned like a blank one.
 * - Anything else is a rename to the trimmed name.
 */
export const decideRuleSetRename = ({
  draft,
  currentName,
  gesture,
}: {
  draft: string;
  currentName: string;
  gesture: 'enter' | 'blur';
}): RuleSetRenameDecision => {
  const next = draft.trim();
  if (next === currentName) return { action: 'close' };
  if (containsInvisibleCharacters(next)) {
    return gesture === 'enter' ? { action: 'hold' } : { action: 'close' };
  }
  if (next === '') return { action: 'close' };

  return { action: 'close', rename: next };
};

/**
 * The probe. 24 characters is where the measured gap is widest while the worst
 * case a user can inflict on themselves stays a fraction of a second: the same
 * patterns cost 4x this at 26 characters and 16x at 28.
 */
const PROBE_INPUT_LENGTH = 24;

/** Milliseconds one probe input may cost. See the docblock for how it was set. */
const PROBE_BUDGET_MS = 10;

/**
 * Several shapes, because a pattern backtracks on the alphabet it is written
 * for — a digit-only run says nothing about `(\w+\s?)*`. Each ends in a
 * character the pattern is unlikely to accept, which is what forces the full
 * backtrack rather than an early match.
 */
const PROBE_INPUTS = [
  `${'a'.repeat(PROBE_INPUT_LENGTH)}!`,
  `${'1'.repeat(PROBE_INPUT_LENGTH)}!`,
  `${'1 '.repeat(PROBE_INPUT_LENGTH / 2)}!`,
  `${'Aa'.repeat(PROBE_INPUT_LENGTH / 2)}!`,
  `${'a@'.repeat(PROBE_INPUT_LENGTH / 2)}!`,
];

/**
 * Past this multiple of the budget a second opinion is not worth its cost. A
 * pause from garbage collection is tens of milliseconds; a catastrophic
 * backtrack at this input length measured 110-304 ms. Between the two there is a
 * band where a retry is worth having, and above it a retry only doubles a delay
 * the user is already feeling.
 */
const RETRY_CEILING = 10;

/**
 * Measured twice inside the band on purpose: a single run can be inflated by a
 * GC pause, and the cost of being wrong here is refusing a pattern the user is
 * entitled to write. One run under budget is enough to acquit.
 */
const withinBudget = (source: string, input: string): boolean => {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const started = performance.now();
    new RegExp(source, 'g').test(input);
    const elapsed = performance.now() - started;

    if (elapsed <= PROBE_BUDGET_MS) {
      return true;
    }

    if (elapsed > PROBE_BUDGET_MS * RETRY_CEILING) {
      return false;
    }
  }

  return false;
};

export const passesComplexityCheck = (pattern: string): boolean => {
  const source = pattern.trim();

  // Nothing to measure, and nothing to say: an empty or uncompilable pattern is
  // already refused by its own check, with its own message. Answering `false`
  // here would put a second, less accurate reason in front of the user.
  if (source === '' || !passesCompileCheck(pattern)) {
    return true;
  }

  return PROBE_INPUTS.every((input) => withinBudget(source, input));
};

/**
 * Both checks judge the TRIMMED pattern, because the editor saves
 * `pattern.trim()` — validating the raw value would let the form disagree with
 * what the runtime receives.
 */
export const passesPresenceCheck = (pattern: string): boolean =>
  pattern.trim() !== '';

export const passesCharactersCheck = (pattern: string): boolean =>
  !containsInvisibleCharacters(pattern.trim());

export const passesCompileCheck = (pattern: string): boolean => {
  try {
    new RegExp(pattern.trim(), 'g');
    return true;
  } catch {
    return false;
  }
};

export const checkMaskingPattern = (
  pattern: string
): MaskingPatternValidity => {
  const passedPresenceCheck = passesPresenceCheck(pattern);
  const passedCompileCheck = passesCompileCheck(pattern);
  const passedComplexityCheck = passesComplexityCheck(pattern);
  const passedCharactersCheck = passesCharactersCheck(pattern);

  return {
    valid:
      passedPresenceCheck &&
      passedCompileCheck &&
      passedComplexityCheck &&
      passedCharactersCheck,
    passedPresenceCheck,
    passedCompileCheck,
    passedComplexityCheck,
    passedCharactersCheck,
  };
};
